"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useVideoThumbnail = void 0;
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _dataDog = require("../../utils/dataDog");
var _SourceItems = require("../../constants/SourceItems");
var _react = require("react");
var SKIP_DURATION_MULTIPLIER = 0.2;
var THUMBNAIL_WIDTH = 200;
var THUMBNAIL_HEIGHT = 112;

/**
 * Using this hook to set the default poster image for uploaded video source
 * @param {*} props
 */
var useVideoThumbnail = exports.useVideoThumbnail = function useVideoThumbnail(props) {
  var videoRef = props.videoRef,
    thumbnail = props.thumbnail,
    onThumbnailReady = props.onThumbnailReady,
    type = props.type,
    isProcessingVideo = props.isProcessingVideo;
  var _useState = (0, _react.useState)({
      width: THUMBNAIL_WIDTH,
      height: THUMBNAIL_HEIGHT
    }),
    _useState2 = (0, _slicedToArray2["default"])(_useState, 2),
    dimension = _useState2[0],
    setDimension = _useState2[1];

  /** Convert to primitive */
  var video = videoRef ? videoRef.current : undefined;
  var canSetVideoThumbnail = (0, _react.useMemo)(function () {
    return video && type === _SourceItems.VIDEO_FILE && !thumbnail;
  }, [thumbnail, type, video]);
  var timerRef = (0, _react.useRef)(null);
  var seekTimeRef = (0, _react.useRef)(null);
  var getDimension = (0, _react.useCallback)(function () {
    var divFactor = dimension.height / THUMBNAIL_HEIGHT;
    if (dimension.width >= dimension.height) {
      divFactor = dimension.width / THUMBNAIL_WIDTH;
    }
    var width = Math.round(dimension.width / divFactor);
    var height = Math.round(dimension.height / divFactor);
    return {
      height: height,
      width: width
    };
  }, [dimension]);
  var onSeek = (0, _react.useCallback)(function () {
    clearTimeout(seekTimeRef.current);
    seekTimeRef.current = setTimeout(function () {
      var canvas = document.createElement('canvas');
      var _getDimension = getDimension(),
        width = _getDimension.width,
        height = _getDimension.height;
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(video, 0, 0, width, height);
      // Using a try catch here to handle the possible error from canvas.toDataUrl
      try {
        var posterThumbnail = canvas.toDataURL('image/png');
        onThumbnailReady(posterThumbnail);
      } catch (e) {
        console.log('OUTPUT Unable to convert canvas to image', e);
      }
    }, 2000);
  }, [onThumbnailReady, video, getDimension]);
  var onLoadedData = (0, _react.useCallback)(function () {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(function () {
      var poster = video.duration * SKIP_DURATION_MULTIPLIER; // skip to 20% of the video
      video.currentTime = poster;

      // we need to play the video to ensure the poster is shown/visible
      var playPromise = video.play();

      /**
       * video.play retuns a promise which can be undefined if its a valid video element with a source to a video
       * that is either not fully loaded or its unavailable. Sometimes it can take a little bit of time before the video is
       * fully loaded
       */
      if (playPromise !== undefined) {
        playPromise.then(function () {
          video.pause();
        })["catch"](function (error) {
          console.log('OUTPUT unable to play video');
          (0, _dataDog.sendToDatadog)('hooks', error, 'Unable to play video', true);
        });
      }
    }, 2000);
  }, [video]);
  (0, _react.useEffect)(function () {
    /** Clear the timeout incase the hook re-runs before the timeout expires */
    return function () {
      return clearTimeout(timerRef.current);
    };
  }, []);
  var onLoadedMetadata = (0, _react.useCallback)(function () {
    var height = video.videoHeight;
    var width = video.videoWidth;
    setDimension({
      height: height,
      width: width
    });
  }, [video]);
  (0, _react.useEffect)(function () {
    if (isProcessingVideo || !canSetVideoThumbnail) return;
    video.addEventListener('loadedmetadata', onLoadedMetadata);
    video.addEventListener('seeked', onSeek);
    video.addEventListener('loadeddata', onLoadedData);
    return function () {
      video.removeEventListener('loadedmetadata', onLoadedMetadata);
      video.removeEventListener('loadeddata', onLoadedData);
      video.removeEventListener('seeked', onSeek);
      clearTimeout(seekTimeRef.current);
    };
  }, [canSetVideoThumbnail, video, onLoadedMetadata, onLoadedData, onSeek]);
};